'use client'

import React, { useState, useEffect, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { GojuonTable } from '@/components/gojuon/GojuonTable'
import { ProgressDisplay } from '@/components/gojuon/ProgressDisplay'
import { Button } from '@/components/ui'
import { Card, CardContent } from '@/components/ui'
import { Crown, Sparkles } from 'lucide-react'
import type { Kana } from '@/data/gojuon'
import { getDefaultTrialVoiceId } from '@/config/trial'
import { TrialRoleModal } from '@/components/trial/TrialRoleModal'
import { TrialPromotionModal } from '@/components/trial/TrialPromotionModal'
import { PlanSelectionModal } from '@/components/subscription/PlanSelectionModal'

// 試用版：使用 localStorage 保存進度（不連接到數據庫）
// 與正式版 useGojuonProgress 邏輯一致，支援 fail 類型和完整數據結構
const useTrialGojuonProgress = () => {
  const [progress, setProgress] = useState<{
    hiragana: Record<string, { learned: boolean; practiced: boolean; written: boolean; failed?: boolean; lastUpdated?: number }>
    katakana: Record<string, { learned: boolean; practiced: boolean; written: boolean; failed?: boolean; lastUpdated?: number }>
  }>({
    hiragana: {},
    katakana: {}
  })

  useEffect(() => {
    // 從 localStorage 載入進度
    const saved = localStorage.getItem('trial_gojuon_progress')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // 處理舊數據兼容性：確保所有項目都有完整的數據結構
        const normalized: typeof progress = {
          hiragana: {},
          katakana: {}
        }
        
        // 正規化 hiragana 數據
        if (parsed.hiragana) {
          Object.keys(parsed.hiragana).forEach(key => {
            normalized.hiragana[key] = {
              learned: parsed.hiragana[key].learned || false,
              practiced: parsed.hiragana[key].practiced || false,
              written: parsed.hiragana[key].written || false,
              failed: parsed.hiragana[key].failed || false,
              lastUpdated: parsed.hiragana[key].lastUpdated || 0
            }
          })
        }
        
        // 正規化 katakana 數據
        if (parsed.katakana) {
          Object.keys(parsed.katakana).forEach(key => {
            normalized.katakana[key] = {
              learned: parsed.katakana[key].learned || false,
              practiced: parsed.katakana[key].practiced || false,
              written: parsed.katakana[key].written || false,
              failed: parsed.katakana[key].failed || false,
              lastUpdated: parsed.katakana[key].lastUpdated || 0
            }
          })
        }
        
        setProgress(normalized)
      } catch (e) {
        // 載入試用進度失敗，使用預設值
        console.error('Failed to load trial gojuon progress:', e)
      }
    }
  }, [])

  // 自動保存到 localStorage
  useEffect(() => {
    if (Object.keys(progress.hiragana).length > 0 || Object.keys(progress.katakana).length > 0) {
      try {
        localStorage.setItem('trial_gojuon_progress', JSON.stringify(progress))
      } catch (e) {
        console.error('Failed to save trial gojuon progress:', e)
      }
    }
  }, [progress])

  const updateProgress = (
    kanaKey: string,
    type: 'learn' | 'practice' | 'write' | 'fail',
    mode: 'hiragana' | 'katakana'
  ) => {
    setProgress(prev => {
      const newProgress = { ...prev }
      const prevModeMap = newProgress[mode] || {}
      const current = prevModeMap[kanaKey] || {
        learned: false,
        practiced: false,
        written: false,
        failed: false,
        lastUpdated: 0
      }

      // 根據類型更新狀態（與正式版邏輯一致）
      const updated = {
        ...current,
        [type === 'learn' ? 'learned' : type === 'practice' ? 'practiced' : type === 'write' ? 'written' : 'failed']: true,
        // 答對時清除失敗標記（與正式版邏輯一致）
        ...(type === 'practice' ? { failed: false } : {}),
        lastUpdated: Date.now()
      }

      return {
        ...newProgress,
        [mode]: {
          ...prevModeMap,
          [kanaKey]: updated
        }
      }
    })
  }

  return { progress, updateProgress }
}

const GojuonTrialPageContent = () => {
  const router = useRouter()
  const [mode, setMode] = useState<'learn' | 'practice' | 'write'>('learn')
  const searchParams = useSearchParams()
  const [isHiragana, setIsHiragana] = useState(searchParams?.get('mode') !== 'katakana')
  const [isClient, setIsClient] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [showPromotionModal, setShowPromotionModal] = useState(false)
  const [showPlanModal, setShowPlanModal] = useState(false)
  const { progress, updateProgress } = useTrialGojuonProgress()

  const openPlanModal = () => {
    setShowPlanModal(true)
  }

  const closePlanModal = () => {
    setShowPlanModal(false)
  }


  useEffect(() => {
    setIsClient(true)
    
    // 檢查今天是否第一次訪問
    const today = new Date().toISOString().split('T')[0]
    const visitKey = `trial_gojuon_first_visit_${today}`
    const roleSelectedKey = `trial_gojuon_role_selected_${today}`
    const hasVisitedToday = localStorage.getItem(visitKey)
    const hasSelectedRoleToday = localStorage.getItem(roleSelectedKey)
    
    if (!hasVisitedToday) {
      // 第一次訪問：顯示角色選擇彈窗
      setShowRoleModal(true)
      // 記錄今天已訪問（但不標記為已選擇角色）
      localStorage.setItem(visitKey, 'true')
    } else if (hasVisitedToday && hasSelectedRoleToday) {
      // 第二次及以上訪問且已選擇角色：顯示推廣提示框
      setShowPromotionModal(true)
    }
    // 如果已訪問但未選擇角色，不顯示任何彈窗（讓用戶可以繼續選擇）
    
    // 如果 Suspense 提前完成，繼續在背景預載入剩餘音檔
    // （主要預載入在 GojuonLoadingScreen 中進行）
    const continuePreload = async () => {
      try {
        const voiceId = getDefaultTrialVoiceId()
        const { preloadGojuonAudio } = await import('@/utils/gojuonAudioPreloader')
        // 在背景繼續預載入（不顯示進度，避免干擾用戶）
        await preloadGojuonAudio(voiceId)
      } catch (error) {
        // 預載入失敗不影響使用
      }
    }
    
    // 延遲一點開始，確保主要預載入在載入畫面中進行
    setTimeout(() => {
      continuePreload()
    }, 1000)
  }, [])

  const handleSelectTrialRole = () => {
    // 選擇試用角色後關閉彈窗
    setShowRoleModal(false)
    // 記錄今天已選擇角色（用於判斷是否顯示推廣提示框）
    const today = new Date().toISOString().split('T')[0]
    const roleSelectedKey = `trial_gojuon_role_selected_${today}`
    localStorage.setItem(roleSelectedKey, 'true')
  }

  const handleCloseModal = () => {
    // 關閉彈窗（用戶可以稍後再選擇）
    setShowRoleModal(false)
  }

  const handleShowRoleModal = () => {
    // 從推廣提示框打開角色選擇彈窗
    setShowPromotionModal(false)
    setShowRoleModal(true)
  }

  const handleClosePromotionModal = () => {
    // 關閉推廣提示框
    setShowPromotionModal(false)
  }

  const handleKanaSelect = (kana: Kana) => {
    const kanaKey = isHiragana ? kana.hiragana : kana.katakana
    updateProgress(kanaKey, 'learn', isHiragana ? 'hiragana' : 'katakana')
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-8">
      {/* 試用角色選擇彈窗 */}
      {showRoleModal && (
        <TrialRoleModal
          onSelect={handleSelectTrialRole}
          onClose={handleCloseModal}
        />
      )}

      {/* 推廣提示框 */}
      {showPromotionModal && (
        <TrialPromotionModal
          onShowRoleModal={handleShowRoleModal}
          onClose={handleClosePromotionModal}
        />
      )}

      {/* 方案選擇彈窗 */}
      <PlanSelectionModal
        isOpen={showPlanModal}
        onClose={closePlanModal}
      />

      <div className="max-w-4xl mx-auto">
        {/* 試用版提示橫幅 */}
        <Card className="mb-6 border-yellow-500 bg-yellow-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-yellow-400" />
                <span className="font-semibold">50音完全免費</span>
                <span className="text-sm text-gray-300">N5~N1 學習內容需訂閱解鎖</span>
              </div>
              <Button
                onClick={openPlanModal}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                <Crown className="h-4 w-4 mr-2" />
                立即訂閱解鎖 N5~N1
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 頂部標題 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold">五十音練習（試用版）</h1>
        </div>

        {isClient && <ProgressDisplay progress={progress} mode={isHiragana ? 'hiragana' : 'katakana'} />}

        <div className="mb-8">
          {/* 學習標準說明 */}
          <div className="bg-gray-800 p-4 rounded-lg text-sm mb-6">
            <h3 className="font-semibold mb-2">學習標準說明：</h3>
            <ul className="space-y-2">
              <li className="flex items-center">
                <div className="w-4 h-4 rounded bg-gray-700 mr-2" />
                <span><strong>已學習</strong>：點擊並聆聽過發音</span>
              </li>
              <li className="flex items-center">
                <div className="w-4 h-4 rounded bg-gray-500 mr-2" />
                <span><strong>已練習</strong>：完成書寫練習</span>
              </li>
            <li className="flex items-center">
              <div className="w-4 h-4 rounded bg-mastered mr-2" />
              <span><strong>已掌握</strong>：識別練習正確</span>
            </li>
            <li className="flex items-center">
              <div className="w-4 h-4 rounded bg-failed mr-2" />
              <span><strong>錯誤</strong>：識別練習錯誤（答對後會自動清除）</span>
            </li>
            </ul>
          </div>

          {/* 模式和假名類型選擇 */}
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex gap-2">
              <Button
                onClick={() => setMode('learn')}
                variant={mode === 'learn' ? 'selected' : 'default'}
              >
                學習模式
              </Button>
              <Button
                onClick={() => router.push('/try/gojuon/write')}
                variant="default"
              >
                書寫練習
              </Button>
              <Button
                onClick={() => router.push('/try/gojuon/practice')}
                variant="default"
              >
                識別練習
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setIsHiragana(true)}
                variant={isHiragana ? 'selected' : 'default'}
              >
                平假名
              </Button>
              <Button
                onClick={() => setIsHiragana(false)}
                variant={!isHiragana ? 'selected' : 'default'}
              >
                片假名
              </Button>
            </div>
          </div>
        </div>

        {isClient && mode === 'learn' && (
          <GojuonTable 
            mode={isHiragana ? 'hiragana' : 'katakana'} 
            onSelect={handleKanaSelect}
            progress={progress[isHiragana ? 'hiragana' : 'katakana']}
            showDakuten={true}
            isTrial={true} // 標記為試用版
            trialVoiceId={getDefaultTrialVoiceId()} // 使用試用版預設語音
          />
        )}

        {/* 底部訂閱引導 */}
        <Card className="mt-8 border-purple-500 bg-purple-900/20">
          <CardContent className="p-6 text-center">
            <h3 className="text-xl font-bold mb-2">想要學習 N5~N1？</h3>
            <p className="text-gray-300 mb-4">
              訂閱後可解鎖 N5~N1 完整內容<br />
              單字、文法、漢字、模擬考試<br />
              以及更多語音角色選擇
            </p>
            <div className="flex justify-center">
              <Button
                onClick={openPlanModal}
                variant="secondary"
                className="w-full sm:w-auto"
              >
                立即訂閱
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  )
}

// 載入畫面組件（包含音檔預載入）
const GojuonLoadingScreen = () => {
  const [preloadProgress, setPreloadProgress] = useState<{ loaded: number; total: number } | null>(null)

  useEffect(() => {
    const startPreload = async () => {
      try {
        const voiceId = getDefaultTrialVoiceId()
        const { preloadGojuonAudio } = await import('@/utils/gojuonAudioPreloader')
        
        // 開始預載入並顯示進度
        await preloadGojuonAudio(voiceId, (loaded, total) => {
          setPreloadProgress({ loaded, total })
        })
      } catch (error) {
        // 音檔預載入失敗，不影響使用
      }
    }

    startPreload()
  }, [])

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4">
      <div className="flex space-x-2 mb-4">
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
      <p className="text-gray-300 mb-2">載入中...</p>
      {preloadProgress && (
        <div className="w-64 max-w-full mt-2">
          <div className="text-xs text-gray-400 mb-1 text-center">
            預載入音檔中... {preloadProgress.loaded} / {preloadProgress.total}
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5">
            <div 
              className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(preloadProgress.loaded / preloadProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const GojuonTrialPage = () => {
  return (
    <Suspense fallback={<GojuonLoadingScreen />}>
      <GojuonTrialPageContent />
    </Suspense>
  )
}

export default GojuonTrialPage

